import {
  BoundingBoxGizmo,
  Color3,
  GizmoManager,
  Mesh,
  PointerEventTypes,
  StandardMaterial,
  UtilityLayerRenderer,
  Vector3,
} from '@babylonjs/core';
import { AdvancedDynamicTexture, Button, Checkbox, Control, Rectangle, StackPanel, TextBlock } from '@babylonjs/gui';
import { AssetObject } from '../AssetObject';
import { Game } from '../Game';
import { Level } from '../Level';
import { AssetObjectConfig } from '../MeshFactory';
import { Gui } from '../gui/Gui';

/**
 * Gizmo modes available for manipulation
 */
type GizmoMode = 'combined' | 'boundingBox' | 'position' | 'scale' | 'rotation';

const GIZMO_MODES: GizmoMode[] = ['combined', 'boundingBox', 'position', 'scale', 'rotation'];
const GIZMO_MODE_LABELS: Record<GizmoMode, string> = {
  combined: '🔀 Combined',
  boundingBox: '📦 BoundingBox',
  position: '↔️ Position',
  scale: '📐 Scale',
  rotation: '🔄 Rotation',
};

interface PlacedDecoration {
  mesh: Mesh;
  assetName: string;
  assetType: string;
  position: Vector3;
  rotation: Vector3;
  scale: Vector3;
}

/**
 * Interactive decoration placement tool for SceneViewer mode.
 * Allows placing decoration instances with absolute positioning.
 * The resulting placements can be exported as TypeScript or JSON for use in DecorationDistributionConfig.absolutePlacements.
 */
export class DecorationPlacementTool {
  private placedDecorations: PlacedDecoration[] = [];
  private selectedAssetObject: AssetObject | null = null;
  private availableAssetObjects: AssetObject[] = [];
  private gizmoManager: GizmoManager;
  private boundingBoxGizmo: BoundingBoxGizmo | null = null;
  private gui: AdvancedDynamicTexture;
  private countText: TextBlock;
  private assetText: TextBlock;
  private gizmoModeText: TextBlock;
  private currentGizmoMode: GizmoMode = 'combined';

  // Visual settings
  private readonly SELECTED_COLOR = new Color3(0, 1, 0);
  private readonly SELECTED_ALPHA = 0.7;

  private previouslySelectedMesh: Mesh | null = null;
  private physicsEnabled: boolean = false; // Physics toggle for distributions

  constructor(private level: Level) {}

  /**
   * Initialize the decoration placement tool
   */
  initialize(): void {
    this.collectAssetObjects();
    this.createGizmoManager();
    this.createGUI();
    this.setupPointerEvents();
    this.setupKeyboardEvents();
  }

  /**
   * Collect available asset objects from decoration config
   */
  private collectAssetObjects(): void {
    const decorationConfig = this.level.config.decoration;
    if (decorationConfig?.assetObjects) {
      this.availableAssetObjects = [...decorationConfig.assetObjects];
    }

    // Also collect from obstacle and enemy configs for more options
    if (this.level.config.obstacle?.assetObjects) {
      this.availableAssetObjects.push(...this.level.config.obstacle.assetObjects);
    }

    if (this.availableAssetObjects.length > 0) {
      this.selectedAssetObject = this.availableAssetObjects[0];
    }

    console.log(`[DecorationPlacementTool] Found ${this.availableAssetObjects.length} asset objects`);
  }

  /**
   * Create the gizmo manager for manipulating placed decorations
   */
  private createGizmoManager(): void {
    this.gizmoManager = new GizmoManager(this.level.scene);
    this.gizmoManager.usePointerToAttachGizmos = false;

    // Start with combined mode gizmos enabled
    this.gizmoManager.positionGizmoEnabled = true;
    this.gizmoManager.rotationGizmoEnabled = true;
    this.gizmoManager.scaleGizmoEnabled = true;

    // Update stored values when gizmo manipulation ends
    if (this.gizmoManager.gizmos.positionGizmo) {
      this.gizmoManager.gizmos.positionGizmo.onDragEndObservable.add(() => this.updateSelectedDecoration());
    }
    if (this.gizmoManager.gizmos.rotationGizmo) {
      this.gizmoManager.gizmos.rotationGizmo.onDragEndObservable.add(() => this.updateSelectedDecoration());
    }
    if (this.gizmoManager.gizmos.scaleGizmo) {
      this.gizmoManager.gizmos.scaleGizmo.onDragEndObservable.add(() => this.updateSelectedDecoration());
    }
  }

  /**
   * Update the stored values for the currently selected decoration
   */
  private updateSelectedDecoration(): void {
    const attachedMesh = this.gizmoManager.attachedMesh;
    if (!attachedMesh) return;

    const decoration = this.placedDecorations.find((d) => d.mesh === attachedMesh);
    if (decoration) {
      decoration.position = attachedMesh.position.clone();

      // Babylon.js gizmos use rotationQuaternion, so we need to convert to Euler
      if (attachedMesh.rotationQuaternion) {
        decoration.rotation = attachedMesh.rotationQuaternion.toEulerAngles();
      } else {
        decoration.rotation = attachedMesh.rotation.clone();
      }

      decoration.scale = attachedMesh.scaling.clone();
    }
  }

  /**
   * Create the GUI panel
   */
  private createGUI(): void {
    this.gui = AdvancedDynamicTexture.CreateFullscreenUI('DecorationPlacementUI', true, this.level.scene);

    // Main panel
    const panel = new Rectangle('decorationPlacementPanel');
    panel.width = '250px';
    panel.height = '310px';
    panel.cornerRadius = 10;
    panel.color = 'white';
    panel.thickness = 2;
    panel.background = Gui.controlBackground;
    panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    panel.left = '10px';
    panel.top = '300px';
    this.gui.addControl(panel);

    // Stack panel for content
    const stack = new StackPanel('contentStack');
    stack.width = '230px';
    panel.addControl(stack);

    // Title
    const title = new TextBlock('title', '🎨 Decoration Placer');
    title.height = '20px';
    title.fontSize = 18;
    title.color = 'white';
    title.fontWeight = 'bold';
    stack.addControl(title);

    // Current asset display
    this.assetText = new TextBlock('asset', `Asset: ${this.selectedAssetObject?.asset || 'None'}`);
    this.assetText.height = '25px';
    this.assetText.fontSize = 12;
    this.assetText.color = 'orange';
    stack.addControl(this.assetText);

    // Placed count
    this.countText = new TextBlock('count', 'Placed: 0');
    this.countText.height = '25px';
    this.countText.fontSize = 14;
    this.countText.color = 'lightgray';
    stack.addControl(this.countText);

    // Gizmo mode display
    this.gizmoModeText = new TextBlock('gizmoMode', `Gizmo: ${GIZMO_MODE_LABELS[this.currentGizmoMode]}`);
    this.gizmoModeText.height = '25px';
    this.gizmoModeText.fontSize = 12;
    this.gizmoModeText.color = 'cyan';
    stack.addControl(this.gizmoModeText);

    // Spacer
    this.addSpacer(stack, '10px');

    // Prev/Next asset buttons
    const navRow = new StackPanel('navRow');
    navRow.isVertical = false;
    navRow.height = '35px';
    navRow.width = '200px';
    stack.addControl(navRow);

    const prevBtn = Button.CreateSimpleButton('prevBtn', '◀ Prev');
    prevBtn.width = '95px';
    prevBtn.height = '30px';
    prevBtn.color = 'white';
    prevBtn.background = '#555';
    prevBtn.cornerRadius = 5;
    prevBtn.onPointerUpObservable.add(() => this.selectPreviousAsset());
    navRow.addControl(prevBtn);

    const btnNavSpacer = new Rectangle('btnSpacer');
    btnNavSpacer.width = '10px';
    btnNavSpacer.height = '35px';
    btnNavSpacer.thickness = 0;
    btnNavSpacer.background = 'transparent';
    navRow.addControl(btnNavSpacer);

    const nextBtn = Button.CreateSimpleButton('nextBtn', 'Next ▶');
    nextBtn.width = '95px';
    nextBtn.height = '30px';
    nextBtn.color = 'white';
    nextBtn.background = '#555';
    nextBtn.cornerRadius = 5;
    nextBtn.onPointerUpObservable.add(() => this.selectNextAsset());
    navRow.addControl(nextBtn);

    // Spacer
    this.addSpacer(stack, '10px');

    // Button row for Generate and Clear
    const actionRow = new StackPanel('actionRow');
    actionRow.isVertical = false;
    actionRow.height = '35px';
    actionRow.width = '200px';
    stack.addControl(actionRow);

    // Generate button
    const generateBtn = Button.CreateSimpleButton('generateBtn', 'Generate');
    generateBtn.width = '95px';
    generateBtn.height = '35px';
    generateBtn.color = 'white';
    generateBtn.background = 'green';
    generateBtn.cornerRadius = 5;
    generateBtn.onPointerUpObservable.add(() => this.createCode());
    actionRow.addControl(generateBtn);

    // Small spacer between buttons
    const btnSpacer = new Rectangle('btnSpacer');
    btnSpacer.width = '10px';
    btnSpacer.height = '35px';
    btnSpacer.thickness = 0;
    btnSpacer.background = 'transparent';
    actionRow.addControl(btnSpacer);

    // Clear button
    const clearBtn = Button.CreateSimpleButton('clearBtn', 'Clear');
    clearBtn.width = '95px';
    clearBtn.height = '35px';
    clearBtn.color = 'white';
    clearBtn.background = '#c0392b';
    clearBtn.cornerRadius = 5;
    clearBtn.onPointerUpObservable.add(() => this.clearAllDecorations());
    actionRow.addControl(clearBtn);

    // Spacer
    this.addSpacer(stack, '10px');

    // Help text
    const helpText = new TextBlock(
      'help',
      'Ctrl+Click: Place | Click: Select\nRight-click: Delete | X: Gizmo Mode\nR/T: Rotate -/+15° | Shift+R/T: ±90°'
    );
    helpText.height = '45px';
    helpText.fontSize = 11;
    helpText.color = 'gray';
    stack.addControl(helpText);

    // Spacer
    this.addSpacer(stack, '10px');

    // Physics checkbox row
    const physicsRow = new StackPanel('physicsRow');
    physicsRow.isVertical = false;
    physicsRow.height = '20px';
    physicsRow.width = '200px';
    stack.addControl(physicsRow);

    const physicsCheckbox = new Checkbox('physicsCheckbox');
    physicsCheckbox.width = '20px';
    physicsCheckbox.height = '20px';
    physicsCheckbox.isChecked = this.physicsEnabled;
    physicsCheckbox.color = 'green';
    physicsCheckbox.onIsCheckedChangedObservable.add((value) => {
      this.physicsEnabled = value;
    });
    physicsRow.addControl(physicsCheckbox);

    const physicsLabel = new TextBlock('physicsLabel', '  Physics enabled');
    physicsLabel.width = '150px';
    physicsLabel.height = '20px';
    physicsLabel.fontSize = 12;
    physicsLabel.color = 'white';
    physicsLabel.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    physicsRow.addControl(physicsLabel);
  }

  private addSpacer(stack: StackPanel, height: string): void {
    const spacer = new Rectangle('spacer');
    spacer.height = height;
    spacer.thickness = 0;
    stack.addControl(spacer);
  }

  /**
   * Select the previous asset object
   */
  private selectPreviousAsset(): void {
    if (this.availableAssetObjects.length === 0) return;
    const currentIndex = this.availableAssetObjects.indexOf(this.selectedAssetObject!);
    const newIndex = (currentIndex - 1 + this.availableAssetObjects.length) % this.availableAssetObjects.length;
    this.selectedAssetObject = this.availableAssetObjects[newIndex];
    this.updateAssetText();
  }

  /**
   * Select the next asset object
   */
  private selectNextAsset(): void {
    if (this.availableAssetObjects.length === 0) return;
    const currentIndex = this.availableAssetObjects.indexOf(this.selectedAssetObject!);
    const newIndex = (currentIndex + 1) % this.availableAssetObjects.length;
    this.selectedAssetObject = this.availableAssetObjects[newIndex];
    this.updateAssetText();
  }

  private updateAssetText(): void {
    const index = this.availableAssetObjects.indexOf(this.selectedAssetObject!) + 1;
    const total = this.availableAssetObjects.length;
    this.assetText.text = `Asset: ${this.selectedAssetObject?.asset || 'None'} (${index}/${total})`;
  }

  /**
   * Setup pointer events for placing/selecting/removing decorations
   */
  private setupPointerEvents(): void {
    this.level.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
        const pickResult = this.level.scene.pick(this.level.scene.pointerX, this.level.scene.pointerY, (mesh) => mesh.isEnabled());

        if (!pickResult || !pickResult.hit || !pickResult.pickedPoint) {
          return;
        }

        const pickedMesh = pickResult.pickedMesh as Mesh;

        // Check if we clicked on a placed decoration
        const clickedDecoration = this.placedDecorations.find((d) => d.mesh === pickedMesh);

        if (clickedDecoration) {
          if (pointerInfo.event.button === 2) {
            // Right-click: delete
            this.removeDecoration(clickedDecoration);
          } else if (pointerInfo.event.button === 0) {
            // Left-click: select for gizmo
            this.selectDecoration(clickedDecoration);
          }
          return;
        }

        // Ctrl + Left-click: place new decoration
        if (pointerInfo.event.button === 0 && pointerInfo.event.ctrlKey && pickResult.pickedPoint) {
          if (!this.selectedAssetObject) {
            console.warn('[DecorationPlacementTool] No asset selected');
            return;
          }
          this.placeDecoration(pickResult.pickedPoint);
        }
      }
    });
  }

  /**
   * Setup keyboard events
   */
  private setupKeyboardEvents(): void {
    this.level.scene.onKeyboardObservable.add((kbInfo) => {
      if (kbInfo.type === 1) {
        // Key down
        switch (kbInfo.event.key.toLowerCase()) {
          case 'x':
            this.cycleGizmoMode();
            break;
          case 'escape':
            this.gizmoManager.attachToMesh(null);
            this.detachBoundingBoxGizmo();
            break;
          case 'r':
            this.rotateSelectedDecoration(kbInfo.event.shiftKey ? -Math.PI / 2 : -Math.PI / 12);
            break;
          case 't':
            this.rotateSelectedDecoration(kbInfo.event.shiftKey ? Math.PI / 2 : Math.PI / 12);
            break;
        }
      }
    });
  }

  /**
   * Rotate the currently selected decoration around the Y-axis by the given angle (radians)
   */
  private rotateSelectedDecoration(angleY: number): void {
    if (!this.previouslySelectedMesh) return;

    const mesh = this.previouslySelectedMesh;

    // Ensure we work with Euler rotation (not quaternion)
    if (mesh.rotationQuaternion) {
      mesh.rotation = mesh.rotationQuaternion.toEulerAngles();
      mesh.rotationQuaternion = null;
    }

    mesh.rotation.y += angleY;
    this.updateSelectedDecoration();
  }

  /**
   * Cycle through available gizmo modes
   */
  private cycleGizmoMode(): void {
    const currentIndex = GIZMO_MODES.indexOf(this.currentGizmoMode);
    const nextIndex = (currentIndex + 1) % GIZMO_MODES.length;
    this.currentGizmoMode = GIZMO_MODES[nextIndex];
    this.applyGizmoMode();
    this.gizmoModeText.text = `Gizmo: ${GIZMO_MODE_LABELS[this.currentGizmoMode]}`;
    console.log(`[DecorationPlacementTool] Gizmo mode: ${this.currentGizmoMode}`);
  }

  /**
   * Apply the current gizmo mode settings
   */
  private applyGizmoMode(): void {
    const selectedMesh = this.previouslySelectedMesh;

    // Detach from mesh first
    this.gizmoManager.attachToMesh(null);
    this.detachBoundingBoxGizmo();

    // Disable all gizmos first
    this.gizmoManager.positionGizmoEnabled = false;
    this.gizmoManager.rotationGizmoEnabled = false;
    this.gizmoManager.scaleGizmoEnabled = false;

    // Enable appropriate gizmos based on mode
    switch (this.currentGizmoMode) {
      case 'combined':
        this.gizmoManager.positionGizmoEnabled = true;
        this.gizmoManager.rotationGizmoEnabled = true;
        this.gizmoManager.scaleGizmoEnabled = true;
        break;
      case 'boundingBox':
        // BoundingBox uses its own gizmo, not gizmoManager
        break;
      case 'position':
        this.gizmoManager.positionGizmoEnabled = true;
        break;
      case 'scale':
        this.gizmoManager.scaleGizmoEnabled = true;
        break;
      case 'rotation':
        this.gizmoManager.rotationGizmoEnabled = true;
        break;
    }

    // Re-attach to current mesh if any
    if (selectedMesh) {
      if (this.currentGizmoMode === 'boundingBox') {
        this.attachBoundingBoxGizmo(selectedMesh);
      } else {
        this.gizmoManager.attachToMesh(selectedMesh);
      }
    }
  }

  /**
   * Attach bounding box gizmo to mesh
   */
  private attachBoundingBoxGizmo(mesh: Mesh): void {
    this.detachBoundingBoxGizmo();
    const utilLayer = UtilityLayerRenderer.DefaultUtilityLayer;
    this.boundingBoxGizmo = new BoundingBoxGizmo(Color3.FromHexString('#00FF00'), utilLayer);
    this.boundingBoxGizmo.scaleRatio = 1.5; // Prevent gizmo from being hidden by mesh
    this.boundingBoxGizmo.attachedMesh = mesh;
    this.boundingBoxGizmo.onScaleBoxDragEndObservable.add(() => this.updateSelectedDecoration());
    this.boundingBoxGizmo.onRotationSphereDragEndObservable.add(() => this.updateSelectedDecoration());
  }

  /**
   * Detach bounding box gizmo
   */
  private detachBoundingBoxGizmo(): void {
    if (this.boundingBoxGizmo) {
      this.boundingBoxGizmo.dispose();
      this.boundingBoxGizmo = null;
    }
  }

  /**
   * Place a new decoration at the given position
   */
  private placeDecoration(position: Vector3): void {
    if (!this.selectedAssetObject) return;

    // Create a copy of the asset object with clone enabled
    const assetObjectCopy = new AssetObject(this.selectedAssetObject.asset);
    assetObjectCopy.type = this.selectedAssetObject.type;
    assetObjectCopy.clone = true; // Force clone to get a fresh instance

    const meshName = `placed_${this.selectedAssetObject.asset}_${this.placedDecorations.length}`;

    const config = AssetObjectConfig.builder().object(assetObjectCopy).name(meshName).build();

    const mesh = config.buildMesh(this.level);
    if (!mesh) {
      console.warn('[DecorationPlacementTool] Failed to create mesh for:', this.selectedAssetObject.asset);
      return;
    }

    // Explicitly set position, visibility and pickability
    mesh.position = position.clone();
    mesh.isVisible = true;
    mesh.setEnabled(true);
    mesh.isPickable = true;
    mesh.metadata = { isPlacedDecoration: true };

    // Log for debugging
    console.log(`[DecorationPlacementTool] Mesh created: ${mesh.name}, visible: ${mesh.isVisible}, enabled: ${mesh.isEnabled()}`);

    const decoration: PlacedDecoration = {
      mesh,
      assetName: this.selectedAssetObject.asset,
      assetType: this.selectedAssetObject.type || this.selectedAssetObject.asset,
      position: mesh.position.clone(),
      rotation: mesh.rotation.clone(),
      scale: mesh.scaling.clone(),
    };

    this.placedDecorations.push(decoration);
    this.updateCountText();
    this.selectDecoration(decoration);

    console.log(`[DecorationPlacementTool] Placed ${this.selectedAssetObject.asset} at ${position.toString()}`);
  }

  /**
   * Select a decoration for gizmo manipulation
   */
  private selectDecoration(decoration: PlacedDecoration): void {
    // Deselect previous mesh (restore alpha)
    if (this.previouslySelectedMesh && this.previouslySelectedMesh !== decoration.mesh) {
      const prevMat = this.previouslySelectedMesh.material as StandardMaterial;
      if (prevMat) {
        prevMat.alpha = 1.0;
        prevMat.emissiveColor = Color3.Black();
      }
    }

    // Set current mesh as selected
    this.previouslySelectedMesh = decoration.mesh;

    // Make selected mesh transparent and highlight
    const mat = decoration.mesh.material as StandardMaterial;
    if (mat) {
      mat.transparencyMode = StandardMaterial.MATERIAL_ALPHABLEND;
      mat.alpha = this.SELECTED_ALPHA;
      mat.emissiveColor = this.SELECTED_COLOR.scale(0.3);
    }

    // Detach all gizmos first
    this.gizmoManager.attachToMesh(null);
    this.detachBoundingBoxGizmo();

    // Attach appropriate gizmo based on mode
    if (this.currentGizmoMode === 'boundingBox') {
      this.attachBoundingBoxGizmo(decoration.mesh);
    } else {
      this.gizmoManager.attachToMesh(decoration.mesh);
    }
  }

  /**
   * Remove a decoration
   */
  private removeDecoration(decoration: PlacedDecoration): void {
    const index = this.placedDecorations.indexOf(decoration);
    if (index === -1) return;

    // Detach gizmo if attached
    if (this.gizmoManager.attachedMesh === decoration.mesh) {
      this.gizmoManager.attachToMesh(null);
    }

    this.placedDecorations.splice(index, 1);
    decoration.mesh.dispose();
    this.updateCountText();

    console.log(`[DecorationPlacementTool] Removed decoration`);
  }

  /**
   * Clear all placed decorations
   */
  private clearAllDecorations(): void {
    this.gizmoManager.attachToMesh(null);
    this.placedDecorations.forEach((d) => d.mesh.dispose());
    this.placedDecorations = [];
    this.updateCountText();
    console.log('[DecorationPlacementTool] Cleared all decorations');
  }

  /**
   * Update the placed count display
   */
  private updateCountText(): void {
    this.countText.text = `Placed: ${this.placedDecorations.length}`;
  }

  /**
   * Create code for the placed decorations
   */
  createCode(): void {
    if (this.placedDecorations.length === 0) {
      console.warn('[DecorationPlacementTool] No decorations to generate.');
      return;
    }

    // Group by asset type (not asset name)
    const groupedByType = new Map<string, PlacedDecoration[]>();
    for (const d of this.placedDecorations) {
      if (!groupedByType.has(d.assetType)) {
        groupedByType.set(d.assetType, []);
      }
      groupedByType.get(d.assetType)!.push(d);
    }

    // Build TypeScript distribution array
    const tsDistributions: string[] = [];
    const jsonDistributions: any[] = [];

    for (const [assetType, decorations] of groupedByType) {
      // TypeScript format for absolutePlacements
      const tsPlacementsArray = decorations
        .map((d) => {
          const pos = `new Vector3(${d.position.x.toFixed(3)}, ${d.position.y.toFixed(3)}, ${d.position.z.toFixed(3)})`;
          const rot = `new Vector3(${d.rotation.x.toFixed(3)}, ${d.rotation.y.toFixed(3)}, ${d.rotation.z.toFixed(3)})`;
          const scl = `new Vector3(${d.scale.x.toFixed(3)}, ${d.scale.y.toFixed(3)}, ${d.scale.z.toFixed(3)})`;
          return `        { position: ${pos}, rotation: ${rot}, scale: ${scl} }`;
        })
        .join(',\n');

      tsDistributions.push(`  {
    types: ['${assetType}'],
    mode: 'absolute',
    physics: ${this.physicsEnabled},
    absolutePlacements: [
${tsPlacementsArray}
    ]
  }`);

      // JSON format
      jsonDistributions.push({
        types: [assetType],
        mode: 'absolute',
        physics: this.physicsEnabled,
        absolutePlacements: decorations.map((d) => ({
          position: {
            x: parseFloat(d.position.x.toFixed(3)),
            y: parseFloat(d.position.y.toFixed(3)),
            z: parseFloat(d.position.z.toFixed(3)),
          },
          rotation: {
            x: parseFloat(d.rotation.x.toFixed(3)),
            y: parseFloat(d.rotation.y.toFixed(3)),
            z: parseFloat(d.rotation.z.toFixed(3)),
          },
          scale: {
            x: parseFloat(d.scale.x.toFixed(3)),
            y: parseFloat(d.scale.y.toFixed(3)),
            z: parseFloat(d.scale.z.toFixed(3)),
          },
        })),
      });
    }

    // Build message string
    let message = '========== Decoration Distribution Output ==========\n\n';
    message += `🎨 Generated ${this.placedDecorations.length} placements in ${groupedByType.size} distribution(s)\n\n`;

    message += '📝 TypeScript Format (for distributions array):\n';
    message += `distributions: [\n${tsDistributions.join(',\n')}\n]\n`;

    message += '\n📋 JSON Format:\n';
    message += JSON.stringify(jsonDistributions, null, 2);

    message += '\n\n=================================================';

    Game.fireMessage(message);
  }

  /**
   * Dispose the tool and clean up resources
   */
  dispose(): void {
    this.clearAllDecorations();
    this.gizmoManager.dispose();
    this.gui.dispose();
  }
}
